import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { CopyPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { FormItem, FormLabel, FormMessage } from '@/Domain/components/Shared/FormCustom/FormCustom';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Spinner } from '@/Domain/components/Shared/Spinner';

const MAX_NAME_LENGTH = 100;

export interface CloneAgentResponse {
  scenario_id: string;
  name: string;
}

/** A copy too large for the request: the agent appears once the worker finishes. */
export interface QueuedCloneResponse {
  job_id: string;
  name: string;
  total_articles: number;
  total_chunks: number;
}

type CloneResult = CloneAgentResponse | QueuedCloneResponse;

const isQueued = (result: CloneResult): result is QueuedCloneResponse => 'job_id' in result;

interface CloneAgentPayload {
  source_scenario_id: string;
  name: string;
  include_knowledge_base: boolean;
  supports_async_clone: true;
}

const cloneAgentSchema = () =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, i18n.t('sidebar_menu:clone.error.name_required'))
      .max(MAX_NAME_LENGTH, i18n.t('sidebar_menu:clone.error.name_too_long')),
    includeKnowledgeBase: z.boolean(),
  });

type CloneAgentForm = z.infer<ReturnType<typeof cloneAgentSchema>>;

export interface CloneAgentSource {
  scenarioId: string;
  name: string;
}

interface CloneAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: CloneAgentSource;
  container?: HTMLElement | null;
  onQueued?: (queued: QueuedCloneResponse) => void;
}

export const CloneAgentDialog = ({
  open,
  onOpenChange,
  source,
  container,
  onQueued,
}: CloneAgentDialogProps) => {
  const { t } = useTranslation(['sidebar_menu', 'common']);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CloneAgentForm>({
    resolver: zodResolver(cloneAgentSchema()),
    defaultValues: { name: '', includeKnowledgeBase: true },
  });

  useEffect(() => {
    if (!open) return;
    reset({
      name: t('clone.default_name', { name: source.name }).slice(0, MAX_NAME_LENGTH),
      includeKnowledgeBase: true,
    });
  }, [open, source.name, reset, t]);

  // The hard nav takes seconds to tear the page down; the button must stay busy until it does.
  const [isNavigating, setIsNavigating] = useState(false);

  const { mutate, isPending } = useMutation<CloneResult, unknown, CloneAgentPayload>({
    mutationKey: [QueryKey.aiAssistants.clone],
    onSuccess: (data) => {
      if (isQueued(data)) {
        // Too large to copy in the request. The agent is hidden until the worker
        // finishes, so there is nowhere to navigate to yet.
        onQueued?.(data);
        onOpenChange(false);
        return;
      }
      setIsNavigating(true);
      // Hard nav, matching how the switcher already changes agent.
      window.location.replace(`/i/${data.scenario_id}`);
    },
    onError: (error: any) => {
      // The API serialises every refusal under errors.root; Axios's own message is
      // only ever "Request failed with status code N", so it must not win.
      toast.error(
        error?.response?.data?.errors?.root?.[0]?.message ||
          error?.response?.data?.detail ||
          t('clone.error.failed'),
      );
    },
  });

  const isBusy = isPending || isNavigating;

  // Block dismissal while the copy runs so the user can't leave mid-request and
  // lose the new agent's id.
  const handleOpenChange = (next: boolean) => {
    if (isBusy && !next) return;
    onOpenChange(next);
  };

  const onSubmit = (values: CloneAgentForm) =>
    mutate({
      source_scenario_id: source.scenarioId,
      name: values.name,
      include_knowledge_base: values.includeKnowledgeBase,
      supports_async_clone: true,
    });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent container={container} data-testid="clone-agent-dialog">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>
              <CopyPlus className="size-4 bg-muted rounded-lg text-foreground p-1.5 box-content" />
              {t('clone.title')}
            </DialogTitle>
            <DialogDescription>{t('clone.description', { name: source.name })}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="clone-agent-name">{t('clone.field.name')}</FormLabel>
                  <Input
                    id="clone-agent-name"
                    {...field}
                    maxLength={MAX_NAME_LENGTH}
                    disabled={isBusy}
                    data-testid="clone-agent-name-input"
                  />
                  <FormMessage error={errors.name} />
                </FormItem>
              )}
            />

            <Controller
              name="includeKnowledgeBase"
              control={control}
              render={({ field }) => (
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    disabled={isBusy}
                    className="mt-0.5"
                    data-testid="clone-agent-include-kb"
                  />
                  <span className="text-sm text-foreground">
                    {t('clone.field.include_knowledge_base')}
                    <span className="block text-xs text-muted-foreground">
                      {t('clone.field.include_knowledge_base_hint')}
                    </span>
                  </span>
                </label>
              )}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              disabled={isBusy}
              onClick={() => handleOpenChange(false)}
            >
              {t('cancel', { ns: 'common' })}
            </Button>
            <Button
              type="submit"
              disabled={isBusy}
              className="relative"
              data-testid="clone-agent-confirm-button"
            >
              <span className={isBusy ? 'invisible' : ''}>{t('clone.button.confirm')}</span>
              {isBusy && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Spinner className="h-4 w-4" />
                </span>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
